import React, {useImperativeHandle, useMemo, useRef, useState} from 'react';
import {classes, style} from './doppe-action-type-products-widget.st.css';
import {listViewCreateDataSource} from '@wix/devzai-utils-react';
import {convertDoppeWixProductToProductsSettings, DoppeActionTypeProducts} from '../doppe-action-type-products';
import {
    DoppeActionType,
    useWidgetRenderingContext
} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeProductBindableWidget
} from '../../doppe-action-type-product/doppe-action-type-product-widget/doppe-action-type-product-widget';
import {
    DoppeActionTypeListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-list-view-item';
import {
    DoppeActionListItemClickBehaviour,
    DoppeAutoProductType,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsAvailable,
    doppeDtoStoreProductPriceDataConvertToActionPriceData,
    doppeExternalProductProviderResolveBusinessEntityReference,
    doppeHideableValueGetValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisible,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppeHideableValueToggleHidden,
    DoppeProductsExternalDataSourceType
} from '../../../client-server-common';
import {
    doppeActionWidgetPerformWidgetContentReplacement
} from '../../../components/doppe-action-widget-utils/doppe-action-widget-utils';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {
    arrayFind,
    arrayFirst,
    arraySkipTake,
    asArray,
    evaluateFunction,
    iterableMapToArray,
    stringReplaceEmptyOrWhiteSpaceValue
} from '@wix/devzai-utils-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {DoppeActionCouponView} from '../../doppe-actions-common/doppe-action-coupon-view/doppe-action-coupon-view';
import {
    useDoppeViewerLangDictionary
} from '../../../components/doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {DoppeViewerActionContext} from '../../../components/doppe-viewer-action-context/doppe-viewer-action-context';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';

export const DoppeActionTypeProductsWidget = React.memo(function DoppeActionTypeProductsWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeProducts.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const {
        defaultListItemIdToRender = null,
        navigationControllerRef,
        renderingInnerStateController
    } = useWidgetRenderingContext();


    const actionSettings = actionContext.action.settings;

    const {
        products,
        layout,
        itemClickBehaviour,
        showButton,

        externalDataSource,
        externalDataSourceItemsCount,

        previewShowCoupon,
        previewShowPrice,
        previewShowDescription,
        previewShowTitle,
        previewProductButtonText,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
        itemShowPrice,
    } = actionSettings;

    const bymoPageContextProps = useBymoPageContextProps();

    const displayedProducts = useMemo(() => {
        if (externalDataSource === null) {
            return products;
        } else {

            const externalProducts = evaluateFunction(() => {
                switch (externalDataSource.type) {
                    case DoppeProductsExternalDataSourceType.LatestWixProducts: {
                        return bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
                            businessEntity: DoppeBusinessEntity.WixAutoProductsList,
                            id: DoppeAutoProductType.Latest
                        }) ?? []
                    }
                }
            })

            return arraySkipTake(externalProducts, 0, externalDataSourceItemsCount).map(doppeDtoProduct => {
                return {
                    ...convertDoppeWixProductToProductsSettings(doppeDtoProduct),
                    // We override the provider and the id in the provider since the values that are fetched
                    // here are already synced with the external provider.
                    productProvider: null,
                    productProviderId: null,
                } satisfies DoppeActionTypeProducts.ProductSettings
            });
        }
    }, [externalDataSource, externalDataSourceItemsCount, products, bymoPageContextProps])

    const [expandedProductId, setExpandedProductId] = useState<DoppeActionTypeProducts.ProductSettings['id'] | null>(defaultListItemIdToRender);

    const currentProductToShow = useMemo(
        () => displayedProducts.find(product => product.id === expandedProductId) ?? null,
        [displayedProducts, expandedProductId]);

    useImperativeHandle(navigationControllerRef, () => {
        return {
            goBack() {
                renderingInnerStateController.setValue(false);
                setExpandedProductId(null);
            },
            canGoBack() {
                return !!expandedProductId;
            }
        }
    })

    const viewrLang = useDoppeViewerLangDictionary();

    const existingProducts = useMemo(() => {
        return iterableMapToArray(displayedProducts, (product, skip) => {

            const {
                productProviderId,
                productProvider
            } = product;

            if (productProvider !== null && productProviderId !== null) {
                const boundProduct = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalProductProviderResolveBusinessEntityReference(productProvider, productProviderId)
                );

                if (doppeBusinessEntityDtoIsAvailable(boundProduct)) {
                    return {
                        ...product,
                        businessEntityDto: boundProduct
                    }
                } else {
                    return skip;
                }
            } else {
                return {
                    ...product,
                    businessEntityDto: undefined
                }
            }
        })
    }, [displayedProducts, bymoPageContextProps]);

    const listViewDataSource = useMemo(() => {
        return listViewCreateDataSource(existingProducts, {
            idFunction: item => item.id
        });
    }, [existingProducts]);

    const rootElementRef = useRef(null);

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
            ref={rootElementRef}
            onBackClick={() => {
                renderingInnerStateController.setValue(false);
                setExpandedProductId(null);
            }}
        >
            {evaluateFunction(() => {
                if (!currentProductToShow) {

                    if (listViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return (
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                layout={layout}
                                hasSeeMoreButton={showButton}
                                onItemClick={eventData => {

                                    const itemId = eventData.id;

                                    switch (itemClickBehaviour) {
                                        case DoppeActionListItemClickBehaviour.PreviewItem: {
                                            doppeActionWidgetPerformWidgetContentReplacement(rootElementRef.current, () => {
                                                renderingInnerStateController.setValue(true);
                                                setExpandedProductId(itemId)
                                            })
                                            break;
                                        }
                                        case DoppeActionListItemClickBehaviour.OpenLink: {

                                            const [product, productIndex] = arrayFind(displayedProducts, item => item.id === itemId);

                                            if (product) {

                                                if (doppeHideableValueIsVisible(product.couponCode)) {
                                                    doppeActionWidgetPerformWidgetContentReplacement(rootElementRef.current, () => {
                                                        renderingInnerStateController.setValue(true);
                                                        setExpandedProductId(itemId)
                                                    })
                                                } else {
                                                    actionContext.reportGoalAchieved(eventData.id, productIndex);
                                                    actionContext.navigateToUrl(product.productPageLink, true);
                                                }
                                            }

                                            break;
                                        }
                                    }
                                }}
                                renderItem={(dataItem, _doppeActionLayout, index) => {

                                    const {
                                        productTitle,
                                        description,
                                        productImage,
                                        priceData,
                                        businessEntityDto,
                                        ribbon
                                    } = dataItem;

                                    const evaluatedPriceData = doppeHideableValueIsVisible(priceData) ?
                                        (
                                            businessEntityDto ?
                                                doppeDtoStoreProductPriceDataConvertToActionPriceData(businessEntityDto.price) :
                                                doppeHideableValueGetValue(priceData)
                                        ) :
                                        undefined;

                                    return (
                                        <DoppeActionTypeListViewItem
                                            actionContext={actionContext as DoppeViewerActionContext<any>}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            title={productTitle}
                                            description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                            image={productImage}
                                            price={evaluatedPriceData && itemShowPrice ? stringReplaceEmptyOrWhiteSpaceValue(evaluatedPriceData.discountPrice, evaluatedPriceData.actualPrice) : undefined}
                                            layout={layout}
                                            isOneItem={listViewDataSource.length === 1}
                                            isLink={itemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink}
                                            ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? doppeHideableValueGetValue(ribbon) : undefined}
                                            hideImageWhenPossible={!itemShowImage}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={listViewDataSource}
                                actionContext={actionContext}
                            />
                        )
                    }
                } else {
                    return (
                        itemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItem ?
                            <DoppeActionTypeProductBindableWidget
                                actionSettings={{
                                    productImage: currentProductToShow.productImage,
                                    couponCode: doppeHideableValueToggleHidden(
                                        currentProductToShow.couponCode,
                                        isHidden => isHidden ? true : !previewShowCoupon
                                    ),
                                    description: doppeHideableValueToggleHidden(
                                        currentProductToShow.description,
                                        isHidden => isHidden ? true : !previewShowDescription
                                    ),
                                    productTitle: doppeHideableValueToggleHidden(
                                        currentProductToShow.productTitle,
                                        isHidden => isHidden ? true : !previewShowTitle
                                    ),
                                    productButtonText: doppeHideableValueGetVisibleValue(
                                        currentProductToShow.productButtonText,
                                        previewProductButtonText
                                    ),
                                    priceData: doppeHideableValueToggleHidden(
                                        currentProductToShow.priceData,
                                        isHidden => isHidden ? true : !previewShowPrice
                                    ),
                                    productPageLink: currentProductToShow.productPageLink,
                                    ribbon: currentProductToShow.ribbon,
                                    productProvider: currentProductToShow.productProvider,
                                    productProviderId: currentProductToShow.productProviderId
                                }}
                                reportGoalAchieved={() => {
                                    actionContext.reportGoalAchieved(
                                        currentProductToShow?.id,
                                        displayedProducts.findIndex(item => item.id === currentProductToShow?.id)
                                    );
                                }}
                                actionId={actionContext.action.id}
                            />
                            :
                            <DoppeActionCouponView
                                couponCode={doppeHideableValueGetValue(currentProductToShow.couponCode)}
                                couponImage={currentProductToShow.productImage ? arrayFirst(asArray(currentProductToShow.productImage)) : undefined}
                                linkUrl={currentProductToShow.productPageLink}
                                couponButtonText={viewrLang.continueToWebsite}
                                description={viewrLang.couponDescription}
                                reportGoalAchieved={() => {
                                    actionContext.reportGoalAchieved(
                                        currentProductToShow?.id,
                                        displayedProducts.findIndex(item => item.id === currentProductToShow?.id)
                                    );
                                }}
                                actionId={actionContext.action.id}
                            />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});

